import Tooltip from '@mui/material/Tooltip';
import { FormattedMessage, useIntl } from 'react-intl';

import * as basicConstants from '../../../constants/basic';
import { FACEBOOK, GOOGLE, LINE, TABOOLA } from '../../../constants/mediaData';
import { dateRangeList } from '../../../utils/date';
import { makeWeekdayLabel, makeBiWeekdayLabel, makeMonthdayLabel } from '../../../utils/sendDateIntl';

/**
 * @param {object} currentData  伺服器回來的報表設定資料
 * @param {'send-list' | 'detail'} type 呼叫場景，用來判斷是否顯示寄送時間
 */

// SendAllEmailsSettings: report_mode 回傳的代號
export const reportModeMapping = {
  last_7d: 0,
  last_14d: 2,
  last_month: 1,
  custom: 3,
};

// SendAllEmailsSettings
export const rangeShortcuts = [
  {
    range: 'last_7d',
    labels: 'common.sendDate.weekly',
  },
  {
    range: 'last_14d',
    labels: 'common.sendDate.bi-weekly',
  },
  {
    range: 'last_month',
    labels: 'common.sendDate.monthly',
  },
  {
    range: 'last_30d',
    labels: 'common.sendDate.last30Days',
  },
  {
    range: 'custom',
    labels: 'common.sendDate.custom',
  }
];

export const initSendAllEmailsSettings = (reportType) => {
  switch (reportType) {
    case 1:
      return {
        reportMode: 'last_month',
        reportDateRange: {
          startReportDate: dateRangeList.last_month.getRange()[0],
          endReportDate: dateRangeList.last_month.getRange()[1]
        }
      };
    case 3:
    case 5:
      return {
        reportMode: 'last_14d',
        reportDateRange: {
          startReportDate: dateRangeList.last_14d.getRange()[0],
          endReportDate: dateRangeList.last_14d.getRange()[1]
        }
      };
    default:
      return {
        reportMode: 'last_7d',
        reportDateRange: {
          startReportDate: dateRangeList.last_7d.getRange()[0],
          endReportDate: dateRangeList.last_7d.getRange()[1]
        }
      };
  }
}

export const HandleSendDate = (currentData, type) => {
  const intl = useIntl();
  const sendType = currentData?.reportType.split(",");

  const reportOrder = [
    {
      key: "0",
      label: intl.formatMessage({ id: "common.sendDate.weekly" }),
      value: () => makeWeekdayLabel(intl, currentData.sendDay)
    },
    {
      key: "2",
      label: intl.formatMessage({ id: "common.sendDate.bi-weekly" }),
      value: () => makeBiWeekdayLabel(intl, currentData.biweekly_day)
    },
    {
      key: "1",
      label: intl.formatMessage({ id: "common.sendDate.monthly" }),
      value: () => makeMonthdayLabel(intl, currentData.monthly)
    },
    {
      key: "4",
      label: intl.formatMessage({ id: "common.sendDate.last30Days" }),
      value: () => makeMonthdayLabel(intl, currentData.monthly)
    }
  ];

  // 依 reportType 組 Tooltip
  const parts = reportOrder
    .filter(report => sendType.includes(report.key))
    .map((report, index) => (
      <Tooltip key={`report-${index}`} title={report.label}>
        <span>{report.value()}</span>
      </Tooltip>
    ));

  // 非 send-list 畫面時再加寄送時間
  if (type !== "send-list") {
    parts.push(
      <Tooltip key="time" title={intl.formatMessage({ id: "common.sendDate.sendTime" })}>
        <span>{currentData.sendTime}</span>
      </Tooltip>
    );
  }

  // 在 Tooltip 間加入 " / "
  return parts.reduce((prev, curr) => (prev === null ? [curr] : [...prev, " / ", curr]), null);
}

export const SEND_DETAIL = {
  [FACEBOOK]: {
    sendDetail: [
      'weeklyReportName',
      'adAccounts',
      'sendDate',
      'toAddresses',
      'mailMessageType',
      'language',
      'excelSheets',
      'fields',
      'testButton',
    ],
    emailTemplate: {
      [basicConstants.EMAIL_TEMPLATE_REACH]: {
        label: 'emailData.template.facebook.type.reachPointGoal',
        fields: []
      },
      [basicConstants.EMAIL_TEMPLATE_COVERSION_CAMPAIGN]: {
        label: 'emailData.template.facebook.type.conversionKeyMetrics',
        fields: [basicConstants.FIELDS_OVERVIEW, basicConstants.FIELDS_CREATIVE, basicConstants.FIELDS_TA]
      },
      [basicConstants.EMAIL_TEMPLATE_COVERSION_METRICS]: {
        label: 'emailData.template.facebook.type.conversionCampaign',
        fields: [basicConstants.FIELDS_CREATIVE, basicConstants.FIELDS_TA]
      },
    },
    emailBlocks: {
      [basicConstants.FIELDS_OVERVIEW]: 'emailData.template.modal.overview',
      [basicConstants.FIELDS_CREATIVE]: 'emailData.template.modal.creativePerformance',
      [basicConstants.FIELDS_TA]: 'emailData.template.modal.audiencePerformance'
    }
  },
  [GOOGLE]: {
    sendDetail: [
      'weeklyReportName',
      'adAccounts',
      'sendDate',
      'toAddresses',
      'mailMessageType',
      'language',
      'conversionsType',
      'excelSheets',
      'fields',
      'testButton',
    ],
    emailTemplate: {
      [basicConstants.EMAIL_TEMPLATE_COVERSION_CAMPAIGN]: {
        label: 'emailData.template.google.overview.title',
        fields: [
          basicConstants.SEARCH_OVERVIEW,
          basicConstants.SEARCH_KEYWORD,
          basicConstants.PERFORMANCE_MAX,
          basicConstants.DEMAND_GEN,
          basicConstants.VIDEO,
          basicConstants.DISPLAY,
        ]
      },
    },
    emailBlocks: {
      [basicConstants.SEARCH_OVERVIEW]: 'emailData.template.google.overview.search',
      [basicConstants.SEARCH_KEYWORD]: 'emailData.template.google.overview.keywords',
      [basicConstants.PERFORMANCE_MAX]: 'emailData.template.google.overview.pmax',
      [basicConstants.DEMAND_GEN]: 'emailData.template.google.overview.demandGen',
      [basicConstants.VIDEO]: 'emailData.template.google.overview.video',
      [basicConstants.DISPLAY]: 'emailData.template.google.overview.display',
    }
  },
  [LINE]: {
    sendDetail: [
      'weeklyReportName',
      'adAccounts',
      'sendDate',
      'toAddresses',
      // 'mailMessageType',
      // 'language',
      'campaignGoals',
      'excelSheets',
      'fields',
      'testButton',
    ],
  },
  [TABOOLA]: {
    sendDetail: [
      'weeklyReportName',
      'adAccounts',
      'sendDate',
      'toAddresses',
      // 'language',
      'excelSheets',
      'fields',
      'testButton',
    ],
  }
}
